<script>
  import { createEventDispatcher } from 'svelte';
  import { Checkbox, InlineNotification, Modal, TextArea } from 'carbon-components-svelte';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';

  import { getServerErrorMessage } from '../../../utils';
  import { approveRestoreRequest } from '../../../services';

  const dispatch = createEventDispatcher();

  export let open = false,
    status,
    restoreRequest,
    blocked = true,
    noSizesDetected = false;

  let resultNotification,
    approvalMessage = null,
    loading = false,
    understand = false;

  async function onFormSubmit() {
    if (!understand || blocked) {
      return;
    }

    resultNotification = null;
    loading = true;

    try {
      const approval = await approveRestoreRequest(restoreRequest.id, { status, approvalMessage });
      loading = false;
      dispatch('close', approval);
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again!';
      console.error('[ApprovalModal] Failed to request restore!', errorMessage);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to request restore! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    if (loading) {
      return;
    }

    dispatch('close');
  }

  function onChange({ target: { value } }) {
    approvalMessage = value;
  }

  function stopPropagation(event) {
    event.stopPropagation();
  }

  function onCheck({ detail: checked }) {
    understand = checked;
  }

  $: isApproved = status === 'Approved';
  $: understand = understand || status === 'Rejected';
</script>

<Modal
  bind:open
  modalHeading={isApproved ? 'Approve Request' : 'Reject Request'}
  primaryButtonText={isApproved ? 'Agree and proceed' : 'Reject'}
  danger={isApproved}
  secondaryButtonText="Cancel"
  shouldSubmitOnEnter={false}
  primaryButtonDisabled={loading || !understand || blocked}
  on:click:button--secondary={handleClose}
  on:close={handleClose}
  on:submit={onFormSubmit}
>
  <LoadingSpinner {loading} small showOverlay>
    {#if isApproved && noSizesDetected}
      <InlineNotification kind="warning" lowContrast hideCloseButton>
        <strong slot="titleChildren">No restorable files were detected for the selected paths.</strong>
        <div slot="subtitleChildren">Are you sure you want to proceed with approving?</div>
      </InlineNotification>
    {/if}
    {#if resultNotification}
      <InlineNotification
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
        hideCloseButton
      />
    {/if}
    {#if isApproved}
      <p class="main">
        Approving this request will trigger the restore process for the selected objects.<br /><strong
          >Please review carefully before you proceed.</strong
        >
      </p>
    {:else}
      <p class="main">Rejecting this request will dismiss the restore process.</p>
    {/if}
    <TextArea
      id="approvalMessage"
      name="approvalMessage"
      labelText={isApproved ? 'Approval Note' : 'Rejection Note'}
      value={approvalMessage}
      placeholder={isApproved ? 'Note for approval..' : 'Reason for rejection..'}
      on:change={onChange}
    />
    {#if isApproved}
      <div class="understand">
        <Checkbox
          labelText="I confirm the selected objects and I understand that this action will trigger the restore process"
          checked={understand}
          on:click={stopPropagation}
          on:check={onCheck}
        />
      </div>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .main {
    margin-bottom: 1rem;
  }

  .understand {
    margin-top: 1rem;
  }
</style>
